import { useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, RefreshCw, Trash2, VolumeX } from "lucide-react";
import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { ConfirmDialog } from "@/components/ConfirmDialog";
import { Button, IconButton, Loading, ProgressBar } from "@/components/kit";
import { PageHeader } from "@/components/layout";
import { ResultActions } from "@/components/render/ResultActions";
import { useToast } from "@/components/toast";
import { api } from "@/lib/api";

const STAGES = {
  queued: "Waiting in line", starting: "Starting", downloading: "Gathering your media", voiceover: "Recording voiceover",
  preparing: "Designing your branding", rendering_segments: "Rendering scenes", compositing: "Mixing music & titles",
  uploading: "Finishing up",
};

/** Player box sized from the real output dimensions: never stretched, never letterboxed by the page. */
function Player({ job }) {
  const ar = (job.result?.width ?? 9) / (job.result?.height ?? 16);
  return (
    <div className="mx-auto overflow-hidden rounded-[20px] border border-border bg-card shadow-2xl"
      style={{ aspectRatio: `${ar}`, width: `min(100%, calc(70vh * ${ar}))` }} data-testid="result-video-player">
      <video src={job.video_url} poster={job.thumb_url ?? undefined} controls playsInline preload="metadata"
        className="h-full w-full bg-black object-contain" data-testid="result-video-element" />
    </div>
  );
}

function Progress({ job, onBack }) {
  return (
    <div className="mx-auto flex max-w-xl flex-col items-center gap-4 py-10 text-center lg:py-20" data-testid="render-progress-state">
      <p className="text-7xl font-medium tracking-tighter text-primary tabular-nums" data-testid="render-progress-text">{job.progress}%</p>
      <ProgressBar value={Math.max(2, job.progress)} className="h-2.5 w-full" />
      <p className="text-sm font-medium" data-testid="render-stage-text">{STAGES[job.stage] ?? "Rendering"}</p>
      <p className="mt-4 text-[13px] text-muted-foreground">You can leave this page or close the browser — your video keeps rendering.</p>
      <Button title="Back to property" variant="secondary" onClick={onBack} className="mt-6 w-full" data-testid="render-leave-button" />
    </div>
  );
}

function Failed({ job, busy, rerun, onBack }) {
  return (
    <div className="mx-auto flex max-w-md flex-col items-center gap-3 py-10 text-center lg:py-20" data-testid="render-failed-state">
      <div className="flex h-[72px] w-[72px] items-center justify-center rounded-full bg-destructive"><AlertTriangle className="h-9 w-9 text-white" aria-hidden /></div>
      <h2 className="text-[28px] font-medium">Video failed</h2>
      <p className="text-base text-card-foreground" data-testid="render-error-text">{job.error}</p>
      <div className="mt-4 flex w-full flex-col gap-3">
        <Button title="Try again" icon={RefreshCw} onClick={() => rerun({}, "retry")} loading={busy === "retry"} data-testid="render-try-again-button" />
        {job.error === "Voiceover failed" ? (
          <Button title="Create without voiceover" variant="secondary" icon={VolumeX} loading={busy === "novo"}
            onClick={() => rerun({ voiceover: false, script: null }, "novo")} data-testid="render-without-voiceover-button" />
        ) : null}
        <Button title="Back to property" variant="ghost" onClick={onBack} data-testid="render-back-to-property-button" />
      </div>
    </div>
  );
}

function Done({ job }) {
  return (
    <div className="grid grid-cols-1 gap-8 lg:grid-cols-[minmax(0,1fr)_300px] lg:items-center">
      <Player job={job} />
      <div className="flex flex-col gap-5">
        <div>
          <p className="eyebrow mb-1">Ready to post</p>
          <p className="text-[13px] text-muted-foreground" data-testid="result-meta-text">
            {job.result ? `${job.result.width}×${job.result.height} · ${job.result.duration.toFixed(1)}s` : ""}
          </p>
        </div>
        <ResultActions job={job} />
      </div>
    </div>
  );
}

export default function RenderPage() {
  const { id } = useParams();
  const qc = useQueryClient();
  const toast = useToast();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(null);
  const [confirm, setConfirm] = useState(false);
  const q = useQuery({
    queryKey: ["render", id],
    queryFn: () => api(`/renders/${id}`),
    refetchInterval: (qq) => (qq.state.data && ["done", "failed"].includes(qq.state.data.status) ? false : 2000),
  });
  const job = q.data;
  const back = () => navigate(job?.project_id ? `/projects/${job.project_id}` : "/projects");

  const rerun = async (overrides, key) => {
    if (!job?.project_id || !job.settings) return;
    setBusy(key);
    try {
      const next = await api(`/projects/${job.project_id}/renders`, { method: "POST", body: { ...job.settings, ...overrides } });
      qc.invalidateQueries({ queryKey: ["project", job.project_id] });
      navigate(`/renders/${next.id}`, { replace: true });
    } catch (e) {
      toast(e.message, "error");
    } finally {
      setBusy(null);
    }
  };

  const remove = async () => {
    setBusy("delete");
    try {
      await api(`/renders/${job.id}`, { method: "DELETE" });
      if (job.project_id) qc.invalidateQueries({ queryKey: ["project", job.project_id] });
      qc.invalidateQueries({ queryKey: ["projects"] });
      setConfirm(false);
      back();
    } catch (e) {
      toast(e.message, "error");
      setBusy(null);
    }
  };

  return (
    <section data-testid="render-screen">
      <PageHeader title={job ? `${job.format} video` : "Video"} back={job?.project_id ? `/projects/${job.project_id}` : "/projects"}
        right={job?.status === "done" ? <IconButton icon={Trash2} label="Delete video" tint="text-destructive" className="hover:bg-destructive/10"
          onClick={() => setConfirm(true)} data-testid="result-delete-button" /> : null} />
      {!job ? (
        q.isError ? <p className="text-base text-card-foreground" data-testid="render-load-error">{q.error.message}</p> : <Loading />
      ) : job.status === "done" ? <Done job={job} />
        : job.status === "failed" ? <Failed job={job} busy={busy} rerun={rerun} onBack={back} />
          : <Progress job={job} onBack={back} />}
      <ConfirmDialog open={confirm} title="Delete this video?" message="The finished video will be removed from this property." confirmLabel="Delete"
        onCancel={() => setConfirm(false)} onConfirm={remove} loading={busy === "delete"} testId="confirm-delete-video-sheet" />
    </section>
  );
}
